import { api, FileUrl, ImportFileRecord } from 'api';
import { rtkQueryApi } from 'store/rtkQueryApi';
import { BulkDataType } from 'types';

type TagType = Extract<Parameters<typeof rtkQueryApi.util.invalidateTags>[0][number], string>;

const bulkApiMap: Record<
  BulkDataType,
  {
    getLastImport: () => ReturnType<typeof api.bulkActions.getLastTrainingImport>;
    getLastTenImports: () => ReturnType<typeof api.bulkActions.getLastTenTrainingImports>;
    uploadImportFile: (payload: { File: File }) => ReturnType<typeof api.import.add>;
    // TODO: Add tags for examinerContactDetails, examinerRoles, examinerCentres,
    // speakingCertifications, writingCertifications once their cached queries are implemented.
    extraInvalidationTags?: TagType[];
  }
> = {
  trainingAndStandardisationSessions: {
    getLastImport: () => api.bulkActions.getLastTrainingImport(),
    getLastTenImports: () => api.bulkActions.getLastTenTrainingImports(),
    uploadImportFile: (p) => api.import.add(p),
    extraInvalidationTags: ['Training'],
  },
  speakingMonitorings: {
    getLastImport: () => api.bulkActions.getLastSpeakingMonitoringImport(),
    getLastTenImports: () => api.bulkActions.getLastTenSpeakingMonitoringImports(),
    uploadImportFile: (p) => api.import.addSpeakingMonitorings(p),
    extraInvalidationTags: ['SpeakingMonitoring'],
  },
  osmMonitoringResults: {
    getLastImport: () => api.bulkActions.getLastOsmMonitoringResultsImport(),
    getLastTenImports: () => api.bulkActions.getLastTenOsmMonitoringResultsImports(),
    uploadImportFile: (p) => api.import.addOsmMonitoringResults(p),
    extraInvalidationTags: ['OsmMonitoring'],
  },
  examinerContactDetails: {
    getLastImport: () => api.bulkActions.getLastExaminerContactDetailsImport(),
    getLastTenImports: () => api.bulkActions.getLastTenExaminerContactDetailsImports(),
    uploadImportFile: (p) => api.import.addExaminerContactDetails(p),
  },
  examinerRoles: {
    getLastImport: () => api.bulkActions.getLastExaminerRolesImport(),
    getLastTenImports: () => api.bulkActions.getLastTenExaminerRolesImports(),
    uploadImportFile: (p) => api.import.addExaminerRoles(p),
  },
  osmMonitoringArchiving: {
    getLastImport: () => api.bulkActions.getLastOsmMonitoringArchivingImport(),
    getLastTenImports: () => api.bulkActions.getLastTenOsmMonitoringArchivingImports(),
    uploadImportFile: (p) => api.import.addOsmMonitoringArchiving(p),
    extraInvalidationTags: ['OsmMonitoring'],
  },
  examinerCentres: {
    getLastImport: () => api.bulkActions.getLastExaminerCentresImport(),
    getLastTenImports: () => api.bulkActions.getLastTenExaminerCentresImports(),
    uploadImportFile: (p) => api.import.addExaminerCentres(p),
  },
  speakingCertifications: {
    getLastImport: () => api.bulkActions.getLastSpeakingCertificationsImport(),
    getLastTenImports: () => api.bulkActions.getLastTenSpeakingCertificationsImports(),
    uploadImportFile: (p) => api.import.addSpeakingCertifications(p),
  },
  writingCertifications: {
    getLastImport: () => api.bulkActions.getLastWritingCertificationsImport(),
    getLastTenImports: () => api.bulkActions.getLastTenWritingCertificationsImports(),
    uploadImportFile: (p) => api.import.addWritingCertifications(p),
  },
  odpAssessments: {
    getLastImport: () => api.bulkActions.getLastOdpAssessmentImport(),
    getLastTenImports: () => api.bulkActions.getLastTenOdpAssessmentImports(),
    uploadImportFile: (p) => api.import.addOdpAssessments(p),
    extraInvalidationTags: ['OdpAssessment'],
  },
  odpMonitorings: {
    getLastImport: () => api.bulkActions.getLastOdpMonitoringImport(),
    getLastTenImports: () => api.bulkActions.getLastTenOdpMonitoringImports(),
    uploadImportFile: (p) => api.import.addOdpMonitorings(p),
    extraInvalidationTags: ['OdpMonitoring'],
  },
};

const bulkActionsApi = rtkQueryApi.injectEndpoints({
  endpoints: (builder) => ({
    getLastImport: builder.query<ImportFileRecord | null, BulkDataType>({
      query: (dataType) => bulkApiMap[dataType].getLastImport(),
      providesTags: (_result, _error, dataType) => [{ type: 'BulkImport', id: dataType }],
    }),

    getLastTenImports: builder.query<ImportFileRecord[], BulkDataType>({
      query: (dataType) => bulkApiMap[dataType].getLastTenImports(),
      providesTags: (_result, _error, dataType) => [{ type: 'BulkImport', id: dataType }],
    }),

    uploadImportFile: builder.mutation<File, { dataType: BulkDataType; file: File }>({
      query: ({ dataType, file }) => bulkApiMap[dataType].uploadImportFile({ File: file }),
      invalidatesTags: (_result, _error, { dataType }) => [
        { type: 'BulkImport', id: dataType },
        ...(bulkApiMap[dataType].extraInvalidationTags ?? []).map((tag) => ({ type: tag })),
      ],
    }),
    getFileUrl: builder.query<FileUrl, string>({
      query: (storageId) => api.file.getFileUrl({ storageId }),
    }),
  }),
});

export const {
  useGetLastImportQuery,
  useGetLastTenImportsQuery,
  useUploadImportFileMutation,
  useLazyGetFileUrlQuery,
} = bulkActionsApi;
